import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";

import {
  fetchCartOrWishlist,
  addToCartOrWishlist,
  deleteFromCartOrWishlist,
  setLocalQty,
  selectCartItems,
  selectCartStatus,
  selectCartError,
  selectUpdatingByProductId,
} from "../redux/slices/cartSlice";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

function loadRazorpay() {
  // If you already added checkout.js in index.html, this will instantly resolve
  if (window.Razorpay) return Promise.resolve(true);

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function Cart() {
  const dispatch = useDispatch();

  const items = useSelector(selectCartItems);
  const status = useSelector(selectCartStatus);
  const errorMsg = useSelector(selectCartError);
  const updatingByProductId = useSelector(selectUpdatingByProductId);

  // auth (needed for userId + token)
  const token = useSelector((s) => s.auth.token);
  const userId = useSelector((s) => s.auth.userId);

  const loading = status === "loading";

  // Payment state
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  // Address state (make inputs functional)
  const [phone, setPhone] = useState("");
  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [zip, setZip] = useState("");

  useEffect(() => {
    dispatch(fetchCartOrWishlist({ type: "all" }));
  }, [dispatch]);

  const getImage = (item) => {
    const fallback =
      "https://images.pexels.com/photos/750952/pexels-photo-750952.jpeg";
    return (Array.isArray(item.images) && item.images[0]) || fallback;
  };

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          sum + Number(it.offeredPrice || 0) * Number(it.quantity || 0),
        0,
      ),
    [items],
  );

  const delivery = 0;
  const total = subtotal + delivery;

  const changeQty = (productId, nextQty) => {
    const qty = Math.max(1, nextQty);

    dispatch(setLocalQty({ productId, quantity: qty }));

    dispatch(addToCartOrWishlist({ type: "cart", productId, quantity: qty }))
      .unwrap()
      .then(() => dispatch(fetchCartOrWishlist({ type: "all" })))
      .catch(() => dispatch(fetchCartOrWishlist({ type: "all" })));
  };

  const removeItem = (productId) => {
    dispatch(deleteFromCartOrWishlist({ type: "cart", productId }))
      .unwrap()
      .then(() => dispatch(fetchCartOrWishlist({ type: "all" })))
      .catch(() => dispatch(fetchCartOrWishlist({ type: "all" })));
  };

  // ---- Razorpay flow ----
  // Now creates a SINGLE order for ALL cart items in one call.
  const createOrder = async (cartItems) => {
    const payload = {
      userId,
      includeGST: false,
      items: cartItems.map((it) => ({
        productId: it.productId,
        quantity: Number(it.quantity || 1),
      })),
      shippingAddress: {
        phone: phone || undefined,
        addressLine1: addr1,
        addressLine2: addr2 || undefined,
        zipcode: zip,
      },
    };

    const res = await fetch(`${API_BASE}/Payment/CreateOrder`, {
      method: "POST",
      headers: {
        Authorization: token, // if your backend expects Bearer: `Bearer ${token}`
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const raw = await res.text();
    let data = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      throw new Error(
        data?.message || data?.error || `CreateOrder failed (${res.status})`,
      );
    }

    return data;
  };

  const confirmPayment = async ({
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  }) => {
    const res = await fetch(`${API_BASE}/Payment/ConfirmPayment`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
      }),
    });

    const raw = await res.text();
    let data = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      throw new Error(
        data?.message || data?.error || `ConfirmPayment failed (${res.status})`,
      );
    }

    return data;
  };

  // Now takes the full cart items list (for notes/description) instead of a single item
  const openRazorpay = async ({ orderPayload, cartItems }) => {
    // You MUST receive these from backend in CreateOrder response:
    // - order_id / razorpayOrderId
    // - amount (in paise usually) + currency OR backend already knows it
    // - key_id (recommended) OR use env var key
    const d = orderPayload?.data ?? orderPayload;

    const orderId =
      d?.razorpayOrderId || d?.razorpay_order_id || d?.orderId || d?.order_id;

    const key = "rzp_live_T56AG8Qlq4ZFfb";

    const amount = d?.amount ?? d?.order?.amount ?? d?.orderAmount ?? undefined; // Razorpay expects paise

    const currency = d?.currency ?? d?.order?.currency ?? "INR";

    if (!orderId)
      throw new Error("CreateOrder response missing Razorpay order id.");
    if (!key)
      throw new Error(
        "Missing Razorpay key. Return it from CreateOrder or set REACT_APP_RAZORPAY_KEY_ID.",
      );

    const title =
      cartItems.length === 1
        ? cartItems[0]?.name || "Order"
        : `${cartItems.length} items`;

    return new Promise((resolve, reject) => {
      const options = {
        key,
        amount, // optional if order_id is provided, but good to pass if you have it
        currency,
        name: "Khayat Group",
        description: title,
        order_id: orderId,
        prefill: {
          contact: phone || "",
        },
        notes: {
          userId,
          productIds: cartItems.map((it) => it.productId).join(","),
        },
        theme: {
          color: "#0d6efd",
        },
        handler: async function (response) {
          try {
            await confirmPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            resolve(response);
          } catch (e) {
            reject(e);
          }
        },
        modal: {
          ondismiss: function () {
            reject(new Error("Payment cancelled by user."));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (resp) {
        reject(new Error(resp?.error?.description || "Payment failed"));
      });
      rzp.open();
    });
  };

  // Checkout now creates ONE order for the whole cart, then opens Razorpay ONCE
  const proceedToCheckout = async () => {
    setPayError("");

    if (!token || !userId) {
      setPayError("Please login first.");
      return;
    }
    if (!items.length) return;

    // Basic address validation (you can customize)
    if (!addr1.trim() || !zip.trim()) {
      setPayError("Please fill Address Line 1 and Zip/Postal Code.");
      return;
    }

    const ok = await loadRazorpay();
    if (!ok) {
      setPayError(
        "Failed to load Razorpay. Please check your internet and try again.",
      );
      return;
    }

    setPaying(true);
    try {
      // Single order for all cart items
      const orderPayload = await createOrder(items);

      await openRazorpay({ orderPayload, cartItems: items });

      // After successful payment, clear all items from cart
      await Promise.all(
        items.map((item) =>
          dispatch(
            deleteFromCartOrWishlist({
              type: "cart",
              productId: item.productId,
            }),
          ).unwrap(),
        ),
      );

      // Refresh cart after checkout
      dispatch(fetchCartOrWishlist({ type: "all" }));
    } catch (e) {
      setPayError(e?.message || "Checkout failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <Header />

      <main className="pb-0 pt-4">
        <section className="container">
          <div className="row g-2">
            <div className="col-lg-8 col-12">
              <div className="mb-4">
                <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                  MY CART
                </span>
                <h2 className="fw-semibold ls-sm text-success fs-3 lh-1 mt-0">
                  Your Shopping Cart
                </h2>
                <div className="mt-n3 pt-2">
                  <img src="/assets/images/line-shap.webp" width="150" alt="" />
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger py-2 px-4 rounded-3">
                  {errorMsg}
                </div>
              ) : null}
              {payError ? (
                <div className="alert alert-danger py-2 px-4 rounded-3">
                  {payError}
                </div>
              ) : null}

              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <div
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                </div>
              ) : null}

              {!loading && !items.length ? (
                <div className="card shadow-none rounded-4 bg-danger-soft">
                  <div className="card-body d-flex align-items-center">
                    <div>
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/9374/9374373.png"
                        width="60"
                      />
                    </div>
                    <div className="ms-3">
                      <h4 className="mb-0 lh-1">Your cart is empty</h4>
                      <p className="text-muted mb-0">
                        Add some products to see them here.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {!loading ? (
                <div>
                  {items.map((item) => {
                    const isUpdating = !!updatingByProductId[item.productId];
                    const lineTotal =
                      Number(item.offeredPrice || 0) *
                      Number(item.quantity || 0);

                    return (
                      <div
                        className="card shadow-none bg-success-soft mb-1 rounded-4"
                        key={item.productId}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <img
                              src={getImage(item)}
                              className="rounded-4 border me-4"
                              alt="Product"
                              width="100"
                              height="100"
                            />

                            <div className="flex-grow-1">
                              <h5 className="mb-1 lh-sm">{item.name}</h5>
                              <p className="text-dark small mb-3 lh-sm text-capitalize">
                                Category &mdash; {item.category}
                                <br />
                                Quantity &mdash; {item.quantity}
                                <br />
                                Price / Unit &mdash; ₹{item.offeredPrice}
                              </p>

                              <div className="d-flex align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() =>
                                    changeQty(
                                      item.productId,
                                      Number(item.quantity) - 1,
                                    )
                                  }
                                  disabled={isUpdating || item.quantity <= 1}
                                >
                                  -
                                </button>

                                <span className="px-3 fw-bold d-inline-flex text-dark align-items-center">
                                  {isUpdating ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    item.quantity
                                  )}
                                </span>

                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() =>
                                    changeQty(
                                      item.productId,
                                      Number(item.quantity) + 1,
                                    )
                                  }
                                  disabled={isUpdating}
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            <div className="text-end">
                              <h4 className="text-dark">₹{lineTotal}</h4>

                              <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => removeItem(item.productId)}
                                disabled={isUpdating || paying}
                                title="Remove"
                              >
                                {isUpdating ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="bi bi-trash2 text-danger fs-4"></i>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <div className="pt-4">
                <div>
                  <div>
                    <h5 className="mb-0">Billing / Shipping Address</h5>
                    <div className="text-muted lh-1 mb-4">
                      Manage Address / Additional Phone No.
                    </div>
                  </div>

                  <div className="card bg-light p-2 mb-4 shadow-none rounded-4">
                    <div className="card-body">
                      <div className="row gx-2 gy-3">
                        <div className="col-lg-6 col-md-12">
                          <label
                            className="form-label"
                            htmlFor="courseCheckoutPhone"
                          >
                            Phone No. <span>(Optional)</span>
                          </label>
                          <input
                            type="text"
                            id="courseCheckoutPhone"
                            className="form-control border-0"
                            placeholder="Phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </div>

                        <div className="col-lg-6 col-md-4">
                          <label
                            className="form-label"
                            htmlFor="courseCheckoutZipcode"
                          >
                            Zip/Postal Code{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <input
                            type="text"
                            id="courseCheckoutZipcode"
                            className="form-control border-0"
                            placeholder="Zip"
                            value={zip}
                            onChange={(e) => setZip(e.target.value)}
                          />
                        </div>
                        <div className="col-lg-6 col-md-12">
                          <label
                            className="form-label"
                            htmlFor="courseCheckoutAddress1"
                          >
                            Address Line 1{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <textarea
                            rows={4}
                            id="courseCheckoutAddress1"
                            className="form-control border-0"
                            placeholder="Address"
                            value={addr1}
                            onChange={(e) => setAddr1(e.target.value)}
                          />
                        </div>

                        <div className="col-lg-6 col-md-12">
                          <label
                            className="form-label"
                            htmlFor="courseCheckoutAddress2"
                          >
                            Address Line 2
                          </label>
                          <textarea
                            rows={4}
                            id="courseCheckoutAddress2"
                            className="form-control border-0"
                            placeholder="Address"
                            value={addr2}
                            onChange={(e) => setAddr2(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-12">
              <div className="card shadow-none rounded-4 p-2 bg-light">
                <div className="card-body">
                  <h4 className="mb-3 fw-semibold">Price Details</h4>

                  <div className="d-flex justify-content-between">
                    <span>Subtotal</span>
                    <span className="text-dark fw-medium">₹{subtotal}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-2 mt-1">
                    <span>Delivery Charges</span>
                    <span className="text-success fw-medium">
                      {delivery === 0 ? "Free" : `₹${delivery}`}
                    </span>
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between mb-3">
                    <span className="fw-semibold text-dark">Total Amount</span>
                    <strong className="text-primary fw-semibold">
                      ₹{total}
                    </strong>
                  </div>

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold rounded-pill mt-1"
                    disabled={!items.length || loading || paying}
                    onClick={proceedToCheckout}
                    type="button"
                  >
                    {paying ? "Make Payment" : "Proceed to Checkout"}
                  </button>

                  <div className="text-muted small text-center mt-2">
                    Payments are processed via Razorpay
                  </div>
                </div>
              </div>

              <div>
                <div className="alert alert-warning mt-2 rounded-4 text-center">
                  <i className="bi bi-info-circle me-1"></i> Earnings will be
                  credited after courier deduction
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Cart;
